import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AuthorizationService } from '@netfoundry-ui/shared/authorization';
import { GatewayFormsService } from '@netfoundry-ui/shared/gateway';
import { Collector, Endpoint, Exports, GatewayCluster } from '@netfoundry-ui/shared/model';
import { CollectorService, ExportersService, ExportsService } from '@netfoundry-ui/shared/netflow';
import {
    FeatureService,
    GatewayClusterService,
    LocationService,
    LoggerService,
    NetworkGroupService,
    RegionService,
    ZitiEnabledService,
} from '@netfoundry-ui/shared/services';
import { RegionTransformPipe } from '@netfoundry-ui/ui/pipes';
import { Subscription } from 'rxjs';

const gatewayType = 'GW';
const exporterType = 'dvn-gateway';

@Component({
    selector: 'app-public-internet-gateway-form',
    templateUrl: './public-internet-gateway-form.component.html',
    styleUrls: ['./public-internet-gateway-form.component.scss', '../gatewayform.component.scss'],
})
export class PublicInternetGatewayFormComponent implements OnInit, OnDestroy {
    @Input() isInline = false;
    @Input() currentOrgId;
    @Input() currentNetworkId;
    @Input() showBreakoutFields = false;
    @Input() hideHaUpgrade = false;
    @Output() back: EventEmitter<boolean> = new EventEmitter();
    @Output() hide: EventEmitter<any> = new EventEmitter();
    @Output() newGateway: EventEmitter<Endpoint> = new EventEmitter();

    model: Endpoint = new Endpoint({});
    public clusterModel = new GatewayCluster({});

    hideHelp = false;

    gatewayTypes = [];
    protectionTypes = [];

    isComplete = false;
    regions: Location[] = [];
    errorName = false;
    errorNameLength = false;
    errorType = false;
    errorLocation = false;
    errorRegion = false;
    errorNextHop = false;
    errorCollector = false;
    errorFrequency = false;
    gateways = [];
    regKey = [];
    attempt = 0;

    isHAGateway = false;

    netflowEnabled = false;

    exportModel = new Exports({});

    collectors = [];
    isBuilding = true;
    provisionedString = 'has been created';
    buildingString = 'is building';
    completedTitleStatusString = this.buildingString;
    timeout = 150;
    processing = false;
    errors = {
        name: false,
        geoRegionId: false,
        azureSiteName: false,
        azureResourceGroupName: false,
        azureVirtualWanId: false,
        publicIpdAddress: false,
        bgpPeeringAddress: false,
        bpgASN: false,
        bgpPeerWeight: false,
        deviceLinkSpeed: false,
        deviceVendor: false,
        deviceModel: false,
        bpgNeighborASN: false,
        bgpPeeringNeighborAddress: false,
    };
    maxLength = '64';
    private updatedRegKeySub = new Subscription();
    private subscription = new Subscription();
    private regionId = '';

    constructor(
        private logger: LoggerService,
        private locationService: LocationService,
        public featureService: FeatureService,
        private clusterService: GatewayClusterService,
        public zitiEnabledService: ZitiEnabledService,
        public gatewayFormService: GatewayFormsService,
        public authorizationService: AuthorizationService,
        private regionService: RegionService,
        private collectorService: CollectorService,
        private exportersService: ExportersService,
        private exportsService: ExportsService,
        private organizationService: NetworkGroupService,
        private regionTransform: RegionTransformPipe
    ) {
        this.exportModel.exporterType = exporterType;
        this.exportModel.exportIntervalSeconds = 20;
    }

    ngOnInit() {
        this.processing = false;
        this.errorName = false;
        this.errorLocation = false;
        this.errorRegion = false;
        this.errorType = false;

        // geo region for gateways
        this.subscription.add(
            this.regionService.get().subscribe((result) => {
                this.regions = this.regionTransform.transform(
                    result as Location[],
                    true,
                    false,
                    false,
                    false,
                    false,
                    false
                );
            })
        );

        if (this.authorizationService.canListNetflowCollectors() && this.authorizationService.canCreateExports()) {
            if (this.authorizationService.canListOrganizations()) {
                this.subscription.add(
                    this.organizationService.get().subscribe((res) => {
                        if (res.length > 1) {
                            this.getCollectors();
                        }
                    })
                );
                this.getCollectors({ networkGroupIdsIn: [this.currentOrgId] });
            } else {
                this.getCollectors({ networkGroupIdsIn: [this.currentOrgId] });
            }
        }

        this.protectionTypes = this.clusterService.getProtectionTypes();
    }

    getCollectors(params?) {
        this.subscription.add(
            this.collectorService.find(params).subscribe((result) => {
                this.collectors = result as Collector[];
            })
        );
    }

    ngOnDestroy() {
        this.updatedRegKeySub.unsubscribe();
        this.subscription.unsubscribe();
    }

    goBack() {
        this.back.emit(true);
    }

    hideForm(response?: unknown) {
        this.hide.emit(response);
    }

    async save() {
        this.logger.info('Saving...');
        if (this.validate()) {
            this.processing = true;
            this.model.endpointType = gatewayType;

            if (this.isHAGateway) {
                const result = await this.gatewayFormService.saveHa(this.clusterModel, this.model);
                this.processing = false;
                if (result != null) {
                    this.clusterModel = new GatewayCluster(result);
                    this.model = new Endpoint(result);
                    this.hideForm();
                }
            } else {
                const result = await this.gatewayFormService.save(this.model);

                this.processing = false;
                if (result != null) {
                    this.model = result;

                    if (!this.netflowEnabled) {
                        this.hideForm();
                    } else {
                        this.subscription.add(
                            this.exportersService
                                .findByNativeId(exporterType, this.model.id, {
                                    networkId: this.currentNetworkId,
                                })
                                .subscribe((result) => {
                                    this.exportModel.exporterKey = result.key;
                                    this.exportModel.networkGroupId = this.currentOrgId;
                                    this.exportModel.name = this.model.name;

                                    this.subscription.add(
                                        this.exportsService.save(this.exportModel).subscribe((result) => {
                                            this.hideForm();
                                        })
                                    );
                                })
                        );
                    }

                    this.newGateway.emit(new Endpoint(this.model));
                }
            }

            if (this.model.endpointType === 'AWSCPEGW') {
                this.gatewayFormService.getAwsRegionId(this.model).then((regionId) => {
                    this.regionId = regionId;
                });
            }
        } else {
            this.logger.info('Gateway form is invalid');
        }
    }

    validate(): boolean {
        let exportForValidation;
        if (this.netflowEnabled) {
            exportForValidation = this.exportModel;
        }

        const validateResult = this.gatewayFormService.validate(
            this.model,
            this.isHAGateway,
            gatewayType,
            undefined,
            exportForValidation
        );

        this.errorName = validateResult.errorName;
        this.errorLocation = validateResult.errorRegion;
        this.errorType = validateResult.errorType;
        this.errorRegion = validateResult.errorRegion;
        this.errorNameLength = validateResult.errorNameLength;
        this.errorNextHop = validateResult.errorNextHop;
        this.errorCollector = validateResult.errorCollector;
        this.errorFrequency = validateResult.errorFrequency;

        return validateResult.isValid;
    }

    toggleHAGateway() {
        this.isHAGateway = !this.isHAGateway;
        if (this.isHAGateway) {
            this.maxLength = '59';
            this.model.name = this.model.name.substring(0, 59);
        } else {
            this.maxLength = '64';
        }
    }

    toggleNetflowEnabled() {
        this.netflowEnabled = !this.netflowEnabled;
    }
}
